'use client';

// import * as Sentry from '@sentry/nextjs'; // Sentry disabled (intentionally).
import NextError from 'next/error';
import { useEffect } from 'react';

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  // Report React render errors to PostHog. `capture_exceptions: true` only
  // catches uncaught window errors and unhandled rejections — render errors
  // get swallowed by React and only surface via error boundaries like this
  // one, so we report them manually. The before_send filter in
  // `lib/analytics.ts` still applies, so chunk-load / third-party noise is
  // dropped before reaching the PostHog dashboard.
  //
  // posthog-js is loaded lazily by `lib/analytics.ts`, so we dynamic-import
  // it here to avoid pulling it into this boundary's chunk unconditionally.
  // If posthog isn't initialized yet (rare — error fired before window.load),
  // the call is a best-effort no-op.
  useEffect(() => {
    void import('posthog-js')
      .then(mod => {
        const posthog = (mod as any).default ?? mod;
        try {
          posthog.captureException(error);
        } catch {
          // best-effort
        }
      })
      .catch(() => {
        // posthog-js failed to load — nothing we can do, page is reloading anyway
      });
  }, [error]);

  // If we reached the global error boundary in production, the app is already unusable.
  // A one-time reload can self-heal common deploy-related failures (stale chunks, cached HTML, etc.).
  useEffect(() => {
    const isProd = process.env.NODE_ENV === 'production';
    if (!isProd) return;

    const KEY = 'app-reloaded';
    const hasReloaded = () => {
      try {
        return window.sessionStorage?.getItem(KEY) === '1';
      } catch {
        return typeof window.name === 'string' && window.name.includes(`${KEY}=1`);
      }
    };
    const markReloaded = () => {
      try {
        window.sessionStorage?.setItem(KEY, '1');
      } catch {
        window.name = `${window.name || ''} ${KEY}=1`;
      }
    };

    if (hasReloaded()) return;
    markReloaded();
    window.location.reload();
  }, []);

  return (
    <html lang="en">
      <body>
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not expose status codes for errors, we simply pass 0 to render a
        generic error message. */}
        <NextError statusCode={0} />
      </body>
    </html>
  );
}
